import { Editor, Node, Path, PathRef, Transforms } from 'slate'

import { CustomEditor } from '../../interface/custom-types'

import { getSubListEntry } from './getSubListEntry'
import { isList, isListItem } from './isListHelper'

const isEmptyListNode = (editor: CustomEditor, path: Path) => {
  const node = Node.get(editor, path)

  if (!isList(node) && !isListItem(node)) {
    return false
  }

  return ![...Node.children(editor, path)].length
}

// Taking the nested list away can leave the list it was kept in without items,
// such a leftover is removed together with the list item wrapping it
const removeEmptyListAncestors = (editor: CustomEditor, path: Path | null) => {
  if (!path || !path.length || !isEmptyListNode(editor, path)) {
    return
  }

  const parentPath = Path.parent(path)

  Transforms.removeNodes(editor, { at: path })
  removeEmptyListAncestors(editor, parentPath)
}

/**
 * Remember the items nested under the given list item. They are kept in the list item
 * right after it, so they are never a part of the selection and would stay on their own
 * level while the item they belong to changes its one.
 */
export const getChildSubListItemRef = (
  editor: CustomEditor,
  listItemPath?: Path | null
): PathRef | undefined => {
  if (!listItemPath) {
    return undefined
  }

  const childSubListItemPath = Path.next(listItemPath)

  if (!getSubListEntry(editor, childSubListItemPath)) {
    return undefined
  }

  return Editor.pathRef(editor, childSubListItemPath)
}

/**
 * Move the remembered nested list back under its parent item, so that the items of that
 * list stay exactly one level deeper than the item they belong to.
 */
export const moveChildSubListUnderItem = (
  editor: CustomEditor,
  listItemRef?: PathRef,
  childSubListItemRef?: PathRef
) => {
  const listItemPath = listItemRef?.current
  const childSubListItemPath = childSubListItemRef?.current

  if (listItemPath && childSubListItemPath) {
    Editor.withoutNormalizing(editor, () => {
      const previousParentRef = Editor.pathRef(editor, Path.parent(childSubListItemPath))

      Transforms.moveNodes(editor, {
        at: childSubListItemPath,
        to: Path.next(listItemPath),
      })

      removeEmptyListAncestors(editor, previousParentRef.unref())
    })
  }

  listItemRef?.unref()
  childSubListItemRef?.unref()
}
